import { useState } from "react";
import "./UsersPage.css";
import { useUsers } from "../../hooks/useUsers";
import { useOrganizations } from "../../hooks/useOrganizations";
import { OrganizationResponse } from "../../models/OrganizationModels";
import { User } from "../../models/User";
import { RoleType } from "../../models/UserModels";
import UserFormModal, { UserFormValues } from "./UserFormModal";
import {
  deleteUserAsync,
  getUserDetailsAsync,
  inviteUserAsync,
  updateUserOrganizationAsync,
} from "../../api/UserApi";
import { Constants } from "../../constants/constants";
import { OrgForms } from "../../config/forms";
import { useAuth } from "../../hooks/useAuth";
import Toast from "../../components/Toast";

const ROLE_TYPE_LABELS: Record<RoleType, string> = {
  [RoleType.PlatformOwner]: "Platform Owner",
  [RoleType.OrganizationOwner]: "Organization Owner",
  [RoleType.Admin]: "Admin",
  [RoleType.Manager]: "Manager",
  [RoleType.Receptionist]: "Receptionist",
  [RoleType.Staff]: "Staff",
};

interface IconProps {
  title?: string;
}

const EditIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const DeleteIcon = ({ title }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden={title ? undefined : true}
  >
    {title && <title>{title}</title>}
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

interface UsersPageProps {
  fixedOrganizationId?: string;
  fixedOrganizationName?: string;
}

const UsersPage = ({
  fixedOrganizationId,
  fixedOrganizationName,
}: UsersPageProps) => {
  const isOrgScoped = Boolean(fixedOrganizationId);

  const { user, hasPrivilege } = useAuth();
  const isPlatformOwner = Boolean(user?.isPlatformOwner);
  const canInviteUsers = hasPrivilege(OrgForms.Users, "canCreate");
  const canEditUsers = hasPrivilege(OrgForms.Users, "canEdit");
  const canDeleteUsers = hasPrivilege(OrgForms.Users, "canDelete");

  const [selectedOrg, setSelectedOrg] = useState<OrganizationResponse | null>(
    null,
  );
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const {
    organizations,
    pagination: orgPagination,
    loading: orgsLoading,
    error: orgsError,
    setPage: setOrgPage,
  } = useOrganizations();

  const activeOrgId = isOrgScoped
    ? fixedOrganizationId
    : selectedOrg?.id;

  const {
    users,
    pagination,
    loading,
    error,
    setPage,
    reload,
  } = useUsers(activeOrgId);

  const startPage = (pagination.page - 1) * pagination.pageSize + 1;
  const endPage = Math.min(
    pagination.page * pagination.pageSize,
    pagination.totalCount,
  );

  const handleInvite = async (form: UserFormValues) => {
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    const organizationId = form.organizationId || activeOrgId || "";

    if (!organizationId) {
      setToast({
        message: "Please select an organization",
        type: "error",
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await inviteUserAsync(token, {
        email: form.email,
        userName: form.userName || undefined,
        contact: form.contact || undefined,
        organizationId,
        roleType: form.role,
        roleName: ROLE_TYPE_LABELS[form.role],
        order: form.order,
        designation: form.designation || undefined,
        qualification: form.qualification || undefined,
        bio: form.bio || undefined,
        isPublic: form.isPublic,
        profilePictureFile: form.profilePictureFile || null,
      });

      if (res.success) {
        setInviteOpen(false);
        setToast({ message: res.message, type: "success" });
        reload();
      } else {
        setToast({
          message: res.message || "Failed to send invitation",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to send invitation. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubmit = async (form: UserFormValues) => {
    if (!editingUser) return;

    if (!editingUser.userOrganizationId) {
      setToast({
        message: "This user has no organization membership to edit.",
        type: "error",
      });
      return;
    }

    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    setSubmitting(true);
    try {
      const res = await updateUserOrganizationAsync(
        token,
        editingUser.userOrganizationId,
        {
          roleType: form.role,
          roleName: ROLE_TYPE_LABELS[form.role],
          userName: form.userName || undefined,
          userId: editingUser.id,
          order: form.order,
          designation: form.designation || undefined,
          qualification: form.qualification || undefined,
          bio: form.bio || undefined,
          isPublic: form.isPublic,
          profilePictureFile: form.profilePictureFile || null,
        },
      );

      if (res.success) {
        setEditingUser(null);
        setToast({ message: res.message, type: "success" });
        reload();
      } else {
        setToast({
          message: res.message || "Failed to update user",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to update user. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = async (user: User) => {
    if (!activeOrgId) return;
    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

    setDetailsLoading(true);
    try {
      const res = await getUserDetailsAsync(token, user.id);
      if (!res.success || !res.result) {
        setToast({
          message: res.message || "Failed to load user details",
          type: "error",
        });
        return;
      }

      const membership = res.result.organizations.find(
        (org) => org.organizationId === activeOrgId,
      );

      if (!membership) {
        setToast({
          message: "This user is not a member of the selected organization.",
          type: "error",
        });
        return;
      }

      setEditingUser({
        ...user,
        userOrganizationId: membership.userOrganizationId,
        roleName: membership.roleName,
        order: membership.order ?? null,
        designation: membership.designation,
        qualification: membership.qualification,
        bio: membership.bio,
        profilePictureUrl: membership.profilePictureUrl,
      });
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to load user details. Please try again.",
        type: "error",
      });
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    const organizationIds = activeOrgId ? [activeOrgId] : [];
    setDeleting(true);
    try {
      const res = await deleteUserAsync(token, deleteTarget.id, organizationIds);

      if (res.success) {
        setDeleteTarget(null);
        setToast({ message: res.message, type: "success" });
        reload();
      } else {
        setToast({
          message: res.message || "Failed to delete user",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to delete user. Please try again.",
        type: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const renderOrgList = () => {
    if (orgsLoading) {
      return <div className="users-org-empty">Loading organizations...</div>;
    }

    if (orgsError) {
      return <div className="users-org-empty">{orgsError}</div>;
    }

    return (
      <>
        {organizations.map((org) => (
          <button
            key={org.id}
            className={`users-org-item ${
              !isOrgScoped && selectedOrg?.id === org.id ? "active" : ""
            }`}
            onClick={() => setSelectedOrg(org)}
          >
            <span className="users-org-avatar">
              {org.name.charAt(0).toUpperCase()}
            </span>
            <span className="users-org-name">{org.name}</span>
          </button>
        ))}

        <div className="users-org-pagination">
          <button
            className="users-org-page-btn"
            disabled={!orgPagination.hasPrevious}
            onClick={() => setOrgPage(orgPagination.page - 1)}
          >
            ‹ Prev
          </button>
          <span>
            {orgPagination.page}/{Math.max(1, orgPagination.totalPages)}
          </span>
          <button
            className="users-org-page-btn"
            disabled={!orgPagination.hasNext}
            onClick={() => setOrgPage(orgPagination.page + 1)}
          >
            Next ›
          </button>
        </div>
      </>
    );
  };

  const renderUserList = () => {
    if (loading) {
      return <div className="users-empty">Loading users...</div>;
    }

    if (error) {
      return (
        <div className="users-empty">
          <p>{error}</p>
          <button className="users-btn users-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      );
    }

    if (users.length === 0) {
      return (
        <div className="users-empty">
          No users found. Click "Add User" to invite one.
        </div>
      );
    }

    return (
      <div className="users-table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role Type</th>
              <th>Status</th>
              <th>Website</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user: User) => (
              <tr key={user.id}>
                <td>
                  <div className="users-name">
                    <span className="users-avatar">
                      {(user.userName || user.email)[0].toUpperCase()}
                    </span>
                    {user.userName || user.email}
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <span className="users-role-badge">
                    {ROLE_TYPE_LABELS[user.systemRole as RoleType] ?? "Unknown"}
                  </span>
                </td>
                <td>
                  <span
                    className={`users-status ${user.isActive ? "active" : "inactive"}`}
                  >
                    {user.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <span
                    className={`users-vis-badge ${
                      user.isPublic ? "visible" : "hidden"
                    }`}
                  >
                    {user.isPublic ? "Visible on website" : "Hidden"}
                  </span>
                </td>
                <td>
                  <div className="users-actions">
                    {canEditUsers && activeOrgId && (
                      <button
                        className="users-icon-btn users-icon-btn--edit"
                        title="Edit"
                        aria-label={`Edit user "${user.userName || user.email}"`}
                        disabled={detailsLoading}
                        onClick={() => handleEditClick(user)}
                      >
                        <EditIcon />
                      </button>
                    )}
                    {canDeleteUsers && (
                      <button
                        className="users-icon-btn users-icon-btn--delete"
                        title="Delete"
                        aria-label={`Delete user "${user.userName || user.email}"`}
                        onClick={() => setDeleteTarget(user)}
                      >
                        <DeleteIcon />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="users-page">
      <div
        className={`users-layout ${
          isPlatformOwner ? "" : "users-layout--no-orgs"
        }`}
      >
        {/* ===================== LEFT PANE ===================== */}
        {isPlatformOwner && (
          <aside className="users-orgs-pane">
            <div className="users-orgs-title">Organizations</div>

            {isOrgScoped ? (
              <button className="users-org-item active">
                <span className="users-org-avatar">
                  {(fixedOrganizationName ?? "O").charAt(0).toUpperCase()}
                </span>
                <span className="users-org-name">
                  {fixedOrganizationName ?? "My Organization"}
                </span>
              </button>
            ) : (
              <>
                <button
                  className={`users-org-item ${selectedOrg === null ? "active" : ""}`}
                  onClick={() => setSelectedOrg(null)}
                >
                  <span className="users-org-avatar">All</span>
                  <span className="users-org-name">All Users</span>
                </button>
                <div className="users-org-list">{renderOrgList()}</div>
              </>
            )}
          </aside>
        )}

        {/* ===================== RIGHT PANE ===================== */}
        <section className="users-main">
          <div className="users-header">
            <div>
              <h2>Users</h2>
              <p>
                {isOrgScoped
                  ? (fixedOrganizationName ?? "My Organization")
                  : selectedOrg
                    ? selectedOrg.name
                    : "All users across organizations"}
              </p>
            </div>
            {canInviteUsers && (
              <button
                className="users-btn users-btn--primary"
                onClick={() => setInviteOpen(true)}
              >
                + Add User
              </button>
            )}
          </div>

          {renderUserList()}

          {!loading && !error && users.length > 0 && (
            <div className="users-pagination">
              <span className="users-pagination__info">
                Showing {startPage}–{endPage} of {pagination.totalCount}
              </span>
              <div className="users-pagination__controls">
                <button
                  className="users-btn users-btn--secondary"
                  disabled={!pagination.hasPrevious}
                  onClick={() => setPage(pagination.page - 1)}
                >
                  Previous
                </button>
                <span className="users-pagination__page">
                  Page {pagination.page} of {Math.max(1, pagination.totalPages)}
                </span>
                <button
                  className="users-btn users-btn--secondary"
                  disabled={!pagination.hasNext}
                  onClick={() => setPage(pagination.page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {deleteTarget && (
        <div className="users-confirm-overlay">
          <div className="users-confirm">
            <h3>Delete User</h3>
            <p>
              Are you sure you want to delete{" "}
              <strong>{deleteTarget.userName || deleteTarget.email}</strong>?
              {activeOrgId
                ? " They will be removed from this organization."
                : " They will be removed from all organizations."}
            </p>
            <div className="users-confirm__actions">
              <button
                className="users-btn users-btn--secondary"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button
                className="users-btn users-btn--danger"
                disabled={deleting}
                onClick={handleDelete}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <UserFormModal
        isOpen={inviteOpen}
        mode="create"
        submitting={submitting}
        organizations={organizations}
        defaultOrganizationId={activeOrgId}
        onClose={() => setInviteOpen(false)}
        onSubmit={handleInvite}
      />

      <UserFormModal
        isOpen={Boolean(editingUser)}
        mode="edit"
        submitting={submitting}
        organizations={organizations}
        defaultOrganizationId={activeOrgId}
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onSubmit={handleEditSubmit}
      />
    </div>
  );
};

export default UsersPage;